'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const navLinks = [
  { labelIT: 'Esperienze & Prezzi', labelEN: 'Experiences & Pricing', hrefIT: '/experiences-pricing', hrefEN: '/en/experiences-pricing' },
  { labelIT: 'Acquista E-Foil ', labelEN: 'E-Foil Boards', hrefIT: '/it/efoil-boards', hrefEN: '/en/efoil-boards' },
  { labelIT: 'Location', labelEN: 'Locations', hrefIT: '/it/location', hrefEN: '/en/locations' },
  { labelIT: 'Prenota', labelEN: 'Book', hrefIT: '/booking', hrefEN: '/en/booking' },
  { labelIT: 'Home', labelEN: 'Home', hrefIT: '/homepage', hrefEN: '/en/homepage' },
];

interface HeaderProps {
  lang?: 'it' | 'en';
}

export default function Header({ lang = 'it' }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLang = () => {
    const newLang = lang === 'it' ? 'en' : 'it';
    const current = pathname ?? '/homepage';

    // Build a full map of IT → EN and EN → IT paths
    const pathMap: Record<string, string> = {
      // Homepage
      '/homepage': '/en/homepage',
      '/en/homepage': '/homepage',
      // Booking
      '/booking': '/en/booking',
      '/en/booking': '/booking',
      // Experiences & Pricing
      '/experiences-pricing': '/en/experiences-pricing',
      '/en/experiences-pricing': '/experiences-pricing',
      // E-Foil Boards
      '/it/efoil-boards': '/en/efoil-boards',
      '/en/efoil-boards': '/it/efoil-boards',
      // Location
      '/it/location': '/en/locations',
      '/en/locations': '/it/location',
      // FAQ
      '/it/faq': '/en/faq',
      '/en/faq': '/it/faq',
      // Waiver
      '/it/waiver': '/en/waiver',
      '/en/waiver': '/it/waiver',
      // Firma (IT only — fallback to homepage)
      '/it/firma': '/en/homepage',
    };

    // Strip query string for lookup, preserve it for redirect
    const [basePath, queryString] = current.split('?');
    const targetBase = pathMap[basePath];
    const targetPath = targetBase
      ? queryString ? `${targetBase}?${queryString}` : targetBase
      : newLang === 'en' ? '/en/homepage' : '/homepage';

    router.push(targetPath);
  };

  const bookingHref = '/booking';
  const isBookingPage = pathname?.includes('booking');
  const isSignaturePage = pathname?.includes('firma') || pathname?.includes('waiver');
  const hideBookingCTA = isBookingPage || isSignaturePage;

  return (
    <>
      <header
        className={`header-sticky ${scrolled ? 'scrolled' : ''}`}
        style={{ height: scrolled ? '68px' : '80px', transition: 'height 0.3s ease' }}
      >
        {/* Seasonal Banner */}
        <div className="seasonal-banner" style={{ fontSize: '12px', padding: '6px 16px' }}>
          <span>{lang === 'it' ? 'Alta stagione in arrivo — prenota in anticipo!' : 'Peak season active — book early!'}</span>
        </div>

        <div
          className="container-main flex items-center justify-between"
          style={{ height: scrolled ? '68px' : '80px' }}
        >
          {/* Logo */}
          <Link href="/homepage" className="flex items-center" style={{ textDecoration: 'none', flexShrink: 0 }}>
            <span className="block md:hidden">
              <AppLogo size={56} priority={true} />
            </span>
            <span className="hidden md:block">
              <AppLogo size={80} priority={true} />
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1" style={{ flex: 1, justifyContent: 'center', padding: '0 24px' }}>
            {navLinks.map((link) => {
              const href = 'hrefIT' in link ? (lang === 'it' ? link.hrefIT! : link.hrefEN!) : (link as { href: string }).href!;
              const isActive =
                pathname?.includes(href) ||
                (href === '/it/location' && pathname?.includes('/location')) ||
                (href === '/en/locations' && pathname?.includes('/locations')) ||
                (href === '/it/waiver' && pathname === '/it/waiver') ||
                (href === '/en/waiver' && pathname === '/en/waiver');
              return (
                <Link
                  key={href}
                  href={href}
                  style={{
                    color: isActive ? '#00D4FF' : 'rgba(240, 247, 255, 0.72)',
                    fontSize: '14px',
                    fontWeight: isActive ? 600 : 500,
                    padding: '7px 14px',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    transition: 'color 0.2s ease, background 0.2s ease',
                    background: isActive ? 'rgba(0, 212, 255, 0.1)' : 'transparent',
                    borderBottom: isActive ? '1px solid rgba(0, 212, 255, 0.4)' : '1px solid transparent',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lang === 'it' ? link.labelIT : link.labelEN}
                </Link>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2 md:gap-3" style={{ flexShrink: 0 }}>
            {/* Language Toggle — always visible */}
            <button
              onClick={toggleLang}
              style={{
                background: 'rgba(240, 247, 255, 0.06)',
                border: '1px solid rgba(240, 247, 255, 0.12)',
                borderRadius: '8px',
                color: '#F0F7FF',
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                padding: '7px 8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                transition: 'border-color 0.2s ease',
              }}
              aria-label="Switch language"
            >
              <span>{lang === 'it' ? '🇮🇹' : '🇬🇧'}</span>
              <span>{lang === 'it' ? 'EN' : 'IT'}</span>
            </button>

            {/* Book CTA — DESKTOP ONLY: hidden on mobile, visible on md+ */}
            {!hideBookingCTA && (
              <span className="hidden md:inline-flex items-center">
                <Link
                  href={bookingHref}
                  className="btn-primary"
                  style={{
                    padding: '9px 20px',
                    fontSize: '13px',
                    letterSpacing: '0.05em',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Icon name="CalendarDaysIcon" size={15} variant="solid" />
                  {lang === 'it' ? 'Prenota ora' : 'Book now'}
                </Link>
              </span>
            )}

            {/* Hamburger — mobile only */}
            <button
              className="flex md:hidden items-center justify-center"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: menuOpen ? 'rgba(0, 212, 255, 0.1)' : 'transparent',
                border: '1px solid rgba(240, 247, 255, 0.1)',
                cursor: 'pointer',
                color: '#F0F7FF',
              }}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <Icon name={menuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={20} />
            </button>
          </div>
        </div>

        {/* Mobile Menu — open state */}
        {menuOpen && (
          <div
            className="md:hidden"
            style={{
              background: 'rgba(11, 31, 46, 0.98)',
              backdropFilter: 'blur(20px)',
              borderTop: '1px solid rgba(240, 247, 255, 0.08)',
              padding: '16px',
            }}
          >
            {navLinks.map((link) => {
              const href = 'hrefIT' in link ? (lang === 'it' ? link.hrefIT! : link.hrefEN!) : (link as { href: string }).href!;
              return (
                <Link
                  key={href}
                  href={href}
                  onClick={() => setMenuOpen(false)}
                  style={{
                    display: 'block',
                    color: 'rgba(240, 247, 255, 0.8)',
                    fontSize: '16px',
                    fontWeight: 500,
                    padding: '14px 12px',
                    borderBottom: '1px solid rgba(240, 247, 255, 0.06)',
                    textDecoration: 'none',
                  }}
                >
                  {lang === 'it' ? link.labelIT : link.labelEN}
                </Link>
              );
            })}

            {/* Prenota ora — inside open mobile menu only */}
            {!hideBookingCTA && (
              <Link
                href={bookingHref}
                onClick={() => setMenuOpen(false)}
                className="btn-primary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginTop: '16px',
                  padding: '14px 20px',
                  fontSize: '15px',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  width: '100%',
                }}
              >
                <Icon name="CalendarDaysIcon" size={17} variant="solid" />
                {lang === 'it' ? 'Prenota ora' : 'Book now'}
              </Link>
            )}
          </div>
        )}
      </header>

      {/* Spacer for seasonal banner + header */}
      <div style={{ height: '112px' }} />
    </>
  );
}